import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { getTenantPublic, type TenantPublic } from "@/api/tenants";
import { qk } from "@/api/queryKeys";
import { config } from "@/app/config";
import { currentHost } from "@/app/tenant";
import { EmptyState } from "@/components/data/empty-state";
import { ErrorState } from "@/components/data/error-state";
import { FullScreenLoadingState } from "@/components/data/loading-state";
import fuelGenieLogo from "@/assets/logo.png";
import { applyThemePreset } from "@/lib/theme-preset";
import { logoUrl, toThemePreset, type ThemePreset } from "@/domain/tenant/brand";

/** The organization the app is branded as: its name, logo and default color preset. */
export type Brand = {
  /** Subdomain slug; `null` on the platform console. */
  slug: string | null;
  name: string;
  /** The tenant's logo, or the Fuel Genie logo until it uploads one. */
  logoUrl: string | null;
  /** True while `logoUrl` is the Fuel Genie stand-in, so the organization's name still needs to be shown. */
  logoIsDefault: boolean;
  /** The tenant's fuel-card artwork; `null` shows the generic card. */
  cardImageUrl: string | null;
  /** The tenant's default preset; a viewer's own choice still wins. */
  themePreset: ThemePreset | null;
};

const PLATFORM_BRAND: Brand = { slug: null, name: "Fuel Genie", logoUrl: fuelGenieLogo, logoIsDefault: true, cardImageUrl: null, themePreset: null };

/** Neutral, tenant-free brand for anything rendered outside the provider (the real app never is). */
const NEUTRAL_BRAND: Brand = { slug: null, name: "Fuel Genie", logoUrl: null, logoIsDefault: false, cardImageUrl: null, themePreset: null };

const BrandContext = createContext<Brand>(NEUTRAL_BRAND);

/** The brand an organization's public details describe (its own logo, or the Fuel Genie stand-in). */
export function brandFromPublic(slug: string, tenant: TenantPublic): Brand {
  const ownLogo = logoUrl(tenant.logoPath, config.supabaseUrl);
  return {
    slug,
    name: tenant.name,
    logoUrl: ownLogo ?? fuelGenieLogo,
    logoIsDefault: ownLogo === null,
    cardImageUrl: logoUrl(tenant.cardImagePath, config.supabaseUrl),
    themePreset: toThemePreset(tenant.themePreset),
  };
}

function useDocumentBranding(brand: Brand | null) {
  useEffect(() => {
    if (!brand) {
      return;
    }
    document.title = brand.name;
    applyThemePreset(brand.themePreset ?? "default");

    const description = `${brand.name} — fuel card and fleet spend management.`;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = description;

    let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (brand.logoUrl) {
      if (!icon) {
        icon = document.createElement("link");
        icon.rel = "icon";
        document.head.appendChild(icon);
      }
      icon.href = brand.logoUrl;
    } else {
      icon?.remove();
    }
  }, [brand]);
}

/**
 * Resolves which tenant the browser is on, before anything else renders (the sign-in page needs
 * the brand before there is a session). An unknown or suspended tenant gets its own screen
 * instead of an app that can't load.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const host = useMemo(() => currentHost(), []);
  const slug = host.kind === "tenant" ? host.slug : null;

  const query = useQuery({
    queryKey: qk.tenant.public(slug ?? ""),
    queryFn: () => getTenantPublic(slug as string),
    enabled: slug !== null,
    staleTime: 5 * 60_000,
  });

  const brand = useMemo<Brand | null>(() => {
    if (host.kind === "platform") {
      return PLATFORM_BRAND;
    }
    const tenant = query.data;
    if (!tenant || tenant.status !== "active") {
      return null;
    }
    return brandFromPublic(host.slug, tenant);
  }, [host, query.data]);

  useDocumentBranding(brand);

  if (host.kind === "tenant") {
    if (query.isPending) {
      return <FullScreenLoadingState />;
    }
    if (query.isError) {
      return (
        <ErrorState
          className="min-h-dvh"
          message="Couldn't load this organization. Check your connection and try again."
          onRetry={() => void query.refetch()}
        />
      );
    }
    if (query.data === null) {
      return (
        <EmptyState
          className="min-h-dvh"
          message="We couldn't find this organization. Check the address you were given."
        />
      );
    }
    if (query.data?.status === "suspended") {
      return (
        <EmptyState
          className="min-h-dvh"
          message="This account is suspended. Please contact your administrator."
        />
      );
    }
  }

  return <BrandContext.Provider value={brand ?? PLATFORM_BRAND}>{children}</BrandContext.Provider>;
}

/** Brands a subtree as one organization (support mode, on the platform address). */
export function BrandOverride({ brand, children }: { brand: Brand; children: ReactNode }) {
  useDocumentBranding(brand);
  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

export function useBrand(): Brand {
  return useContext(BrandContext);
}
